import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { http, restoreSession, setSessionExpiredHandler } from '@/api/http';
import { tokenStore } from '@/api/tokenStore';
import type { CurrentUser, LoginRequest, LoginResponse } from './types';

interface AuthContextValue {
  user: CurrentUser | null;

  /** False once the boot-time session restore has settled, either way. */
  isLoading: boolean;

  isAuthenticated: boolean;

  signIn: (credentials: LoginRequest) => Promise<CurrentUser>;
  signOut: () => Promise<void>;

  /** Re-reads the current user, after a profile or permission change. */
  refreshUser: () => Promise<void>;

  /**
   * True when the user holds a permission.
   *
   * For hiding controls only. The server re-checks everything.
   */
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Owns the authenticated session.
 *
 * On mount it performs ONE silent refresh. That is what makes holding the access
 * token in memory workable: a page reload loses the token, and this restores it
 * from the HttpOnly cookie before anything renders — so the user never sees a
 * flash of the login screen just because they pressed F5.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);

    // The cache holds the previous user's data. Left in place, the next person to
    // sign in on this machine would briefly see it.
    queryClient.clear();
  }, [queryClient]);

  // Registered as a callback rather than imported by the HTTP layer, which would
  // otherwise have to import React state and create a cycle.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      clearSession();

      // A full navigation rather than a router push: the session is gone, and
      // discarding all in-memory state is exactly what we want.
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/login?next=${next}`);
    });
  }, [clearSession]);

  // Boot: restore the session, then load the user.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const token = await restoreSession();

      if (cancelled) return;

      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const { data } = await http.get<CurrentUser>('/auth/me');
        if (!cancelled) setUser(data);
      } catch {
        // The refresh worked but /me did not — the account was deactivated
        // between the two calls. Treated as signed out.
        if (!cancelled) clearSession();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clearSession]);

  const signIn = useCallback(async (credentials: LoginRequest) => {
    const { data } = await http.post<LoginResponse>('/auth/login', credentials);

    tokenStore.set(data.accessToken);
    setUser(data.user);

    return data.user;
  }, []);

  const signOut = useCallback(async () => {
    try {
      await http.post('/auth/logout');
    } catch {
      // A failed sign-out must still clear the client. The token expires on its
      // own shortly, and leaving the user apparently signed in is worse.
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const { data } = await http.get<CurrentUser>('/auth/me');
    setUser(data);
  }, []);

  const can = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      signIn,
      signOut,
      refreshUser,
      can,
    }),
    [user, isLoading, signIn, signOut, refreshUser, can],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

/**
 * Reads the auth context.
 *
 * @throws If used outside {@link AuthProvider} — a wiring mistake that should
 * fail immediately rather than silently render a signed-out UI.
 */
export function useAuth(): AuthContextValue {
  const context = use(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an <AuthProvider>.');
  }

  return context;
}
